import React, { useState, useMemo, createContext, useContext, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { ProjectOutletContext } from '@/types/project';
import { Plus, Search, X, ClipboardPlus, ListTodo } from 'lucide-react';
import { motion, LayoutGroup, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { DragProvider } from '@/contexts/drag/DragContext';
import GoalTaskBoard from '@/components/goal-planner/GoalTaskBoard';
import { TaskRecommendationContainer } from '@/components/goal-planner/TaskRecommendationContainer';
import TaskGenerationContextModal, { type UserContext } from '@/components/goal-planner/TaskGenerationContextModal';
import UnifiedCreationModal from '@/components/shared/UnifiedCreationModal';
import QuickCreatePageModal from '@/components/content-generator/editor/pages/components/QuickCreatePageModal';
import NewPageModal from '@/components/content-generator/editor/pages/components/NewPageModal';
import CreateResearchModal from '@/components/shared/CreateResearchModal';
import WorkbookImageUploadModal from '@/components/ui/upload/WorkbookImageUploadModal';
import { useGoalFilteredTasks } from '@/hooks/tasks/use-goal-filtered-tasks';
import { useEncouragement } from '@/hooks/goal-planner/use-encouragement';
import { Task } from '@/types/tasks';
import { toast } from '@/hooks/errors/use-toast';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import DashboardCard from '@/components/ui/DashboardCard';
import DashboardButton from '@/components/ui/DashboardButton';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ChevronDown, Users } from 'lucide-react';
import { useWorkbookTeamMembers } from '@/hooks/tasks/use-task-assignees';
import { useDebouncedValue } from '@/lib/utils';
import { useTaskContentTracking } from '@/hooks/tasks/use-task-content-tracking';
import { useIsMobile } from '@/hooks/layout/use-mobile';
import { useQuestDetection } from '@/hooks/onboarding/useQuestDetection';

// Sidebar components
import MobileSidebarAccordion from '@/components/ui/MobileSidebarAccordion';
import SidebarHeader from '@/components/ui/SidebarHeader';

  // Empty state imports
  import { EmptyStateLayout } from '@/components/shared/empty-states/EmptyStateLayout';
  import { getAppConfig } from '@/components/shared/empty-states/app-configs';

// Create search and filter context
const TaskFilterContext = createContext<{
  searchQuery: string;
  filteredActiveTasks: Task[];
  filteredCompletedTasks: Task[];
}>({
  searchQuery: '',
  filteredActiveTasks: [],
  filteredCompletedTasks: []
});

export const useTaskFilter = () => useContext(TaskFilterContext);

const ProjectTasksPage: React.FC = () => {
  const { project, goal, agentThreadId, navigationData, registerTaskCreationHandler } = useOutletContext<ProjectOutletContext>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  // Initialize quest detection for this page
  useQuestDetection();
  
  // State management
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  const [taskContextInput, setTaskContextInput] = useState(''); // NEW: Context input for AI task generation
  const [isNewPageModalOpen, setNewPageModalOpen] = useState(false);
  const [isGeneratePageModalOpen, setIsGeneratePageModalOpen] = useState(false);
  const [newPageInitialData, setNewPageInitialData] = useState<{title?: string; request?: string; taskId?: string}>({});
  const [isLoadingPageGeneration, setIsLoadingPageGeneration] = useState(false);
  
  // Research modal state
  const [isCreateResearchModalOpen, setIsCreateResearchModalOpen] = useState(false);
  const [newResearchInitialData, setNewResearchInitialData] = useState<{title?: string; description?: string; taskId?: string}>({});

  // Image modal state
  const [isCreateImageModalOpen, setIsCreateImageModalOpen] = useState(false);
  const [newImageInitialData, setNewImageInitialData] = useState<{title?: string; description?: string; taskId?: string}>({});

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAssigneeId, setSelectedAssigneeId] = useState<string>('all');
  
  // Debounce search query
  const debouncedSearch = useDebouncedValue(searchQuery, 500);
  
  // Get team members for assignee filtering
  const { data: teamMembers = [], isLoading: isLoadingTeam } = useWorkbookTeamMembers(project?.id);

  // Task content tracking hook
  const { updateTaskWithGeneratedContent } = useTaskContentTracking();

  // REMOVED: Automatic cover image generation on page load
  // Cover images are now generated during project creation for optimal performance
  
  // 🎯 UNIFIED: Use single goal-filtered context for all task operations
  const { 
    addTask, 
    activeTasks, 
    completedTasks, 
    isLoading: isTasksLoading,
    refetchTasks
  } = useGoalFilteredTasks(goal?.id || '');
  
  const pages = navigationData?.pages || [];
  
  // Filter tasks based on search query and assignee
  const { filteredActiveTasks, filteredCompletedTasks } = useMemo(() => {
    // Helper function to apply filters to a task list
    const applyFilters = (taskList: Task[]) => {
      // Apply search filter
      let searchFilteredTasks = taskList;
      if (debouncedSearch) {
        const query = debouncedSearch.toLowerCase();
        searchFilteredTasks = taskList.filter(task =>
          task.title.toLowerCase().includes(query) ||
          (task.description && task.description.toLowerCase().includes(query))
        );
      }
      
      // Apply assignee filter
      if (selectedAssigneeId === 'all') {
        return searchFilteredTasks;
      }
      
      // Filter by specific assignee
      return searchFilteredTasks.filter(task => {
        if (!task.assignees || task.assignees.length === 0) {
          return selectedAssigneeId === 'unassigned';
        }
        return task.assignees.some(assignee => assignee.assignee_id === selectedAssigneeId);
      });
    };

    return {
      filteredActiveTasks: applyFilters(activeTasks),
      filteredCompletedTasks: applyFilters(completedTasks)
    };
  }, [activeTasks, completedTasks, debouncedSearch, selectedAssigneeId]);

  // Calculate total filtered tasks for feedback
  const totalFilteredTasks = filteredActiveTasks.length + filteredCompletedTasks.length;
  
  // Calculate task counts per assignee (similar to project task counts)
  const assigneeTaskCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    let totalActiveTasks = 0;
    let unassignedCount = 0;
    
    activeTasks.forEach(task => {
      totalActiveTasks++;
      
      if (!task.assignees || task.assignees.length === 0) {
        unassignedCount++;
      } else {
        task.assignees.forEach(assignee => {
          counts[assignee.assignee_id] = (counts[assignee.assignee_id] || 0) + 1;
        });
      }
    });
    
    return { assigneeCounts: counts, totalTasks: totalActiveTasks, unassignedCount };
  }, [activeTasks]);
  
  // 🎯 NEW: Progress Calculation Logic (from old GoalPage)
  const totalTasks = activeTasks.length + completedTasks.length;
  const completedTasksCount = completedTasks.length;
  
  // Calculate progress based on task completion (single source of truth)
  const calculatedProgress = totalTasks > 0 ? Math.round((completedTasksCount / totalTasks) * 100) : 0;
  
  // Use calculated progress as the primary source, fall back to database progress if no tasks
  const displayProgress = totalTasks > 0 ? calculatedProgress : (goal?.progress ?? 0);
  
  // 🎯 NEW: Generate dynamic encouragement messages based on progress
  const encouragement = useEncouragement(displayProgress, totalTasks, completedTasksCount);
  
  // Empty state detection logic
  const shouldShowCenteredLayout = !isTasksLoading && 
                                   activeTasks.length === 0 && 
                                   completedTasks.length === 0 &&
                                   !debouncedSearch;
  
  // Get app configuration for empty state
  const teamTaskConfig = getAppConfig('project-team-tasks');
  
  // Event handlers
  const handleCreateTask = () => {
    setIsCreateTaskModalOpen(true);
  };
  
  // Register task creation handler with the layout
  useEffect(() => {
    if (registerTaskCreationHandler && goal) {
      registerTaskCreationHandler(handleCreateTask);
    }
    
    // Cleanup: unregister when component unmounts or goal changes
    return () => {
      if (registerTaskCreationHandler) {
        registerTaskCreationHandler(undefined);
      }
    };
  }, [registerTaskCreationHandler, goal]);
  
  // 🎯 SIMPLIFIED: Goal-filtered context handles task creation automatically
  const handleTaskCreated = () => {
    // Goal-filtered addTask handles the creation with goal ID automatically
    setIsCreateTaskModalOpen(false);
  };

  // Handle generating AI content for new pages from tasks
  const handleOpenNewPageWithTaskData = (title?: string, request?: string, taskId?: string) => {
    setNewPageInitialData({ title, request, taskId });
    setIsGeneratePageModalOpen(true);
  };

  // Handle generating AI research from tasks
  const handleOpenNewResearchWithTaskData = (title?: string, description?: string, taskId?: string) => {
    setNewResearchInitialData({ title, description, taskId });
    setIsCreateResearchModalOpen(true);
  };

  // Handle generating AI image from tasks
  const handleOpenNewImageWithTaskData = (title?: string, description?: string, taskId?: string) => {
    setNewImageInitialData({ title, description, taskId });
    setIsCreateImageModalOpen(true);
  };

  // Handle Generate Content button click from QuickCreatePageModal
  const handleGenerateWithAI = (title: string) => {
    console.log('[ProjectTasksPage] Opening Advanced AI Page Creation modal with title:', title);
    setNewPageInitialData({ title });
    setNewPageModalOpen(false); // Close quick create modal
    setIsGeneratePageModalOpen(true); // Open advanced modal
  };

  // Handle research creation completion
  const handleResearchCreated = async (researchId: string) => {
    console.log('[ProjectTasksPage] Research created, navigating to resources page:', researchId);
    
    // Track generated content if this was created from a task
    if (newResearchInitialData.taskId) {
      try {
        await updateTaskWithGeneratedContent(newResearchInitialData.taskId, 'research', researchId);
        console.log('[ProjectTasksPage] Linked task to generated research:', newResearchInitialData.taskId, researchId);
      } catch (error) {
        console.error('[ProjectTasksPage] Failed to link task to research:', error);
      }
    }
    
    // Navigate to the project resources page with research auto-open parameter
    navigate(`/project/${project.id}/resources?openResearch=${researchId}`);
  };

  // Handle image creation completion
  const handleImageCreated = async (imageId: string) => {
    console.log('[ProjectTasksPage] Image created, navigating to images page:', imageId);
    
    // Track generated content if this was created from a task
    if (newImageInitialData.taskId) {
      try {
        await updateTaskWithGeneratedContent(newImageInitialData.taskId, 'image', imageId);
        console.log('[ProjectTasksPage] Linked task to generated image:', newImageInitialData.taskId, imageId);
      } catch (error) {
        console.error('[ProjectTasksPage] Failed to link task to image:', error);
      }
    }
    
    // Navigate to the project images page with image auto-open parameter
    navigate(`/project/${project.id}/images?openImage=${imageId}`);
  };
  
  // REMOVED: handleCoverImageSuggested (now handled on page load)
  // Cover image generation is now automatic when users visit the project page
  
  // REMOVED: Cover Image Upload Handler (now handled automatically)
  // const handleCoverImageUpload = async (file: File): Promise<string> => {
  //   try {
  //     console.log('[ProjectTasksPage] Uploading cover image...');
  //     const uploadedUrl = await uploadCoverImage(file);
  //     console.log('[ProjectTasksPage] Cover image uploaded successfully:', uploadedUrl);
  //     
  //     // Close the modal
  //     setCoverImageModal({ isOpen: false });
  //     
  //     // Show success message
  //     toast({
  //       title: "✨ Cover image uploaded!",
  //       description: "Your project now has a beautiful cover image.",
  //       variant: "default",
  //     });
  //     
  //     return uploadedUrl;
  //   } catch (error) {
  //     console.error('[ProjectTasksPage] Cover image upload failed:', error);
  //     toast({
  //       title: "Upload failed",
  //       description: "Failed to upload cover image. Please try again.",
  //       variant: "destructive",
  //     });
  //     throw error;
  //   }
  // };
  
  if (!goal) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 dark:text-gray-300">
          This project doesn't have an associated goal. Tasks functionality requires a goal to be set up.
        </p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {shouldShowCenteredLayout ? (
        /* Empty State Layout - Using standardized sizing */
        <EmptyStateLayout
          icon={<teamTaskConfig.icon className={`h-8 w-8 ${teamTaskConfig.iconColor}`} />}
          title={teamTaskConfig.emptyStateTitle}
          description="Generate AI-powered tasks to help achieve your project goals and track team progress"
          variant={teamTaskConfig.variant}
          fullHeight={false}
        >
          <div className="w-full max-w-2xl">
            <TaskRecommendationContainer
              goalId={goal.id}
              goalTitle={goal.title}
              goalDescription={goal.description}
              openaiThreadId={goal.openai_thread_id || goal.openaiThreadId}
              className="h-[600px]"
              hideClearSelection={true}
              contextInput={taskContextInput}
              onContextInputChange={setTaskContextInput}
              isInitialGeneration={true}
            />
          </div>
        </EmptyStateLayout>
      ) : (
        /* Normal Two-Column Layout */
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6 min-w-0">
          {/* Left Column - Task Board */}
          <div className="space-y-6 order-2 lg:order-1 min-w-0">
            <DragProvider>
              <TaskFilterContext.Provider value={{ 
                searchQuery: debouncedSearch,
                filteredActiveTasks,
                filteredCompletedTasks 
              }}>
                <GoalTaskBoard 
                  goalId={goal.id}
                  onGenerateContent={handleOpenNewPageWithTaskData}
                  onGenerateResearch={handleOpenNewResearchWithTaskData}
                  onGenerateImage={handleOpenNewImageWithTaskData}
                  isGeneratingContent={isLoadingPageGeneration}
                  onCreateTask={handleCreateTask}
                  selectedAssigneeId={selectedAssigneeId}
                  onAssigneeChange={setSelectedAssigneeId}
                  teamMembers={teamMembers}
                  isLoadingTeam={isLoadingTeam}
                  assigneeTaskCounts={assigneeTaskCounts}
                  workbookId={project.id}
                />
              </TaskFilterContext.Provider>
            </DragProvider>
          </div>
          
          {/* Right Column - AI Suggestions + Progress */}
          <div className="space-y-6 order-1 lg:order-2 min-w-0">
            <MobileSidebarAccordion
              variant="amber"
              persistKey="project-tasks-sidebar"
              defaultExpanded={!isMobile}
              className="h-full"
            >
              <SidebarHeader 
                title="Generate Tasks" 
                icon={ListTodo} 
                variant="amber"
              />
              
              {/* Recommended Tasks Section */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <TaskRecommendationContainer
                    goalId={goal.id}
                    goalTitle={goal.title}
                    goalDescription={goal.description}
                    openaiThreadId={goal.openai_thread_id || goal.openaiThreadId}
                    className="h-[500px]"
                    contextInput={taskContextInput}
                    onContextInputChange={setTaskContextInput}
                  />
                </div>
                
                {/* Progress Tracker Section */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Progress Tracker</h4>
                  <div className="space-y-3 p-4 rounded-lg bg-white/50 dark:bg-gray-800/50 border border-gray-200/50 dark:border-gray-600/30">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Overall Progress</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {displayProgress === 100 ? '🎉 Complete!' : `${100 - displayProgress}% remaining`}
                </span>
              </div>
              <div className="relative">
                <div className="h-3 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden shadow-inner">
                  <motion.div 
                    className={`h-full bg-gradient-to-r ${encouragement.progressGradient} rounded-full shadow-sm`}
                    initial={{ width: 0 }}
                    animate={{ width: `${displayProgress}%` }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                  />
                      </div>
                </div>
              </div>
            </div>

                {/* Motivation Section */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Motivation</h4>
            <AnimatePresence mode="wait">
              <motion.div
                key={`encouragement-${displayProgress}`}
                      className={`p-4 rounded-lg border ${encouragement.cardBg} ${encouragement.cardBorder} shadow-sm`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                      <h5 className={`font-medium text-center ${encouragement.cardColor}`}>
                  {encouragement.message}
                      </h5>
              </motion.div>
            </AnimatePresence>
                </div>
              </div>
            </MobileSidebarAccordion>
          </div>
        </div>
      )}
      
      {/* Modals */}
      
      {/* Create Task Modal */}
      <UnifiedCreationModal
        isOpen={isCreateTaskModalOpen}
        onClose={() => setIsCreateTaskModalOpen(false)}
        onTaskCreated={handleTaskCreated}
        defaultTab="task"
        hideTabs={true}
        goalId={goal?.id}
        context="project"
      />
      
      {/* Quick Create Page Modal */}
      <QuickCreatePageModal
        isOpen={isNewPageModalOpen}
        onClose={() => {
          setNewPageModalOpen(false);
          setNewPageInitialData({});
          setIsLoadingPageGeneration(false);
        }}
        workbookId={project.id}
        onPageCreated={async (pageId) => {
          // Track generated content if this was created from a task
          if (newPageInitialData.taskId) {
            try {
              await updateTaskWithGeneratedContent(newPageInitialData.taskId, 'document', pageId);
              console.log('[ProjectTasksPage] Linked task to generated document:', newPageInitialData.taskId, pageId);
            } catch (error) {
              console.error('[ProjectTasksPage] Failed to link task to document:', error);
            }
          }
          
          // Navigate to the project page editor route
          navigate(`/project/${project.id}/page/${pageId}`);
        }}
        onGenerateWithAI={handleGenerateWithAI}
      />
      
      {/* Generate New Page Modal */}
      <NewPageModal
        isOpen={isGeneratePageModalOpen}
        onClose={() => {
          setIsGeneratePageModalOpen(false);
          setNewPageInitialData({});
          setIsLoadingPageGeneration(false);
        }}
        workbookId={project.id}
        initialTitle={newPageInitialData.title}
        initialRequest={newPageInitialData.request}
        onPageCreated={async (pageId) => {
          // Track generated content if this was created from a task
          if (newPageInitialData.taskId) {
            try {
              await updateTaskWithGeneratedContent(newPageInitialData.taskId, 'document', pageId);
              console.log('[ProjectTasksPage] Linked task to generated document:', newPageInitialData.taskId, pageId);
            } catch (error) {
              console.error('[ProjectTasksPage] Failed to link task to document:', error);
            }
          }
          
          // Navigate to the project page editor route
          navigate(`/project/${project.id}/page/${pageId}`);
        }}
      />

      {/* Create Research Modal */}
      <CreateResearchModal
        isOpen={isCreateResearchModalOpen}
        onClose={() => {
          setIsCreateResearchModalOpen(false);
          setNewResearchInitialData({});
        }}
        goalId={goal.id}
        goalTitle={goal.title}
        goalDescription={goal.description}
        onResearchCreated={handleResearchCreated}
        initialData={newResearchInitialData}
      />

      {/* Create Image Modal */}
      <WorkbookImageUploadModal
        isOpen={isCreateImageModalOpen}
        onClose={() => {
          setIsCreateImageModalOpen(false);
          setNewImageInitialData({});
        }}
        workbookId={project.id}
        onImageUploaded={(imageUrl, altText, imageId) => {
          // Use the image ID for navigation with auto-open functionality
          if (imageId) {
            console.log('[ProjectTasksPage] Image created, navigating to images page with auto-open:', imageId);
            handleImageCreated(imageId);
          } else {
            // Fallback: navigate without auto-open
            console.log('[ProjectTasksPage] Image uploaded, navigating to images page');
            navigate(`/project/${project.id}/images`);
          }
        }}
        initialDescription={newImageInitialData.title ? `${newImageInitialData.title}${newImageInitialData.description ? ': ' + newImageInitialData.description : ''}` : ''}
      />
      
      {/* REMOVED: Cover Image Generation Modal (now automatic) */}
      {/* Cover images are now generated automatically in the background */}
    </div>
  );
};

export default ProjectTasksPage; 